import { getDBAsync } from '@/db';
import type { Round, RoundScore, TrumpSuit } from '@/types';
import { generateId } from '@/lib/utils';

export const roundRepo = {
  /**
   * Get all rounds for a game
   */
  async getByGameId(gameId: string): Promise<Round[]> {
    const db = await getDBAsync();
    const rounds = await db.getAllFromIndex('rounds', 'by-game', gameId);
    return rounds.sort((a, b) => a.roundNumber - b.roundNumber);
  },

  /**
   * Get a round by ID
   */
  async getById(id: string): Promise<Round | undefined> {
    const db = await getDBAsync();
    return db.get('rounds', id);
  },

  /**
   * Create a new round
   */
  async create(
    gameId: string,
    scores: RoundScore[],
    trumpSuit: TrumpSuit,
    trumpCallerId: string
  ): Promise<Round> {
    const db = await getDBAsync();

    // Get next round number
    const existingRounds = await this.getByGameId(gameId);
    const roundNumber = existingRounds.length + 1;

    const round: Round = {
      id: generateId(),
      gameId,
      roundNumber,
      trumpSuit,
      trumpCallerId,
      scores,
      createdAt: new Date(),
    };

    await db.put('rounds', round);
    return round;
  },

  /**
   * Update a round's scores
   */
  async update(id: string, scores: RoundScore[]): Promise<Round | null> {
    const db = await getDBAsync();
    const existing = await db.get('rounds', id);
    if (!existing) {
      return null;
    }

    const updated: Round = {
      ...existing,
      scores,
    };
    await db.put('rounds', updated);
    return updated;
  },

  /**
   * Delete a round
   */
  async delete(id: string): Promise<boolean> {
    const db = await getDBAsync();
    const existing = await db.get('rounds', id);
    if (!existing) {
      return false;
    }
    await db.delete('rounds', id);
    return true;
  },

  /**
   * Delete all rounds for a game
   */
  async deleteByGameId(gameId: string): Promise<number> {
    const rounds = await this.getByGameId(gameId);
    for (const round of rounds) {
      await this.delete(round.id);
    }
    return rounds.length;
  },

  /**
   * Get the last round of a game
   */
  async getLastRound(gameId: string): Promise<Round | null> {
    const rounds = await this.getByGameId(gameId);
    return rounds.length > 0 ? rounds[rounds.length - 1] : null;
  },

  /**
   * Get round count for a game
   */
  async countByGameId(gameId: string): Promise<number> {
    const rounds = await this.getByGameId(gameId);
    return rounds.length;
  },
};
